
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Edit, Trash2 } from "lucide-react";
import { Post } from "@/hooks/posts/types";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";

interface PostCardProps {
  post: Post;
  onEdit?: (post: Post) => void;
  onDelete?: (postId: string) => void;
  showActions?: boolean;
}

export const PostCard = React.memo(({ post, onEdit, onDelete, showActions = false }: PostCardProps) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleEdit = () => {
    if (onEdit) {
      onEdit(post);
    }
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (onDelete) {
      setIsDeleting(true);
      try {
        await onDelete(post.id);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  return (
    <div 
      key={post.id}
      className={`border border-gray-200 rounded-lg p-3 bg-white hover:shadow-md ${post.image ? 'card-has-media' : ''}`}
    >
      <div className="flex items-start space-x-2">
        <img
          src={post.user.avatar}
          alt={post.user.name}
          className="w-8 h-8 rounded-full object-cover"
        />
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-1">
            <span className="font-medium text-green-800 text-sm">{post.user.name}</span>
            <span className="text-gray-500 text-xs">•</span>
            <span className="text-gray-500 text-xs">{post.timestamp}</span>
            {post.visibility && post.visibility !== 'public' && (
              <>
                <span className="text-gray-500 text-xs">•</span>
                <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">
                  {post.visibility === 'private' && '🔒 Private'}
                  {post.visibility === 'followers' && '👥 Followers'}
                  {post.visibility === 'org' && '🏢 Organization'}
                </span>
              </>
            )}
            {post.sentiment && (
              <>
                <span className="text-gray-500 text-xs">•</span>
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  post.sentiment === 'great' ? 'bg-green-100 text-green-700' :
                  post.sentiment === 'help' ? 'bg-red-100 text-red-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {post.sentiment === 'great' ? '🎉 Great' : 
                   post.sentiment === 'help' ? '🆘 Help' : 
                   '📈 Standard'}
                </span>
              </>
            )}
          </div>
          <p className="text-gray-800 text-sm mb-2">{post.content}</p>
          
          {post.whatsHappening && (
            <div className="mb-1.5 p-1.5 bg-blue-50 rounded border-l-2 border-blue-200">
              <p className="text-xs text-blue-800">
                <strong>What's happening:</strong> {post.whatsHappening}
              </p>
            </div>
          )}
          
          {post.challenges && (
            <div className="mb-1.5 p-1.5 bg-yellow-50 rounded border-l-2 border-yellow-200">
              <p className="text-xs text-yellow-800">
                <strong>Challenges:</strong> {post.challenges}
              </p>
            </div>
          )}
          
          <img
            src={post.image}
            alt="Post image"
            className="w-full h-16 object-cover rounded-lg mb-2"
          />
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 text-xs text-gray-600">
              <span>❤️ {post.likes}</span>
              <span>💬 {post.comments}</span>
              {post.harvestWeekMonth && (
                <span>📅 {post.harvestWeekMonth}</span>
              )}
              {post.location?.address && (
                <span>📍 {post.location.address}</span>
              )}
            </div>
            
            {showActions && (
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleEdit}
                  className="h-7 px-2 text-xs"
                >
                  <Edit size={12} />
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleDelete}
                  className="h-7 px-2 text-xs text-red-600 hover:text-red-700 hover:border-red-300"
                >
                  <Trash2 size={12} />
                  Delete
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <ConfirmationDialog
        isOpen={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Post"
        description="Are you sure you want to delete this post? This action cannot be undone and the post cannot be restored."
        confirmText="Delete"
        variant="destructive"
        onConfirm={confirmDelete}
        loading={isDeleting}
      />
    </div>
  );
});

PostCard.displayName = 'PostCard';
